const IS_ACTIVE = 'is-active'
const IS_HIDDEN = 'is-hidden'

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.activeColor = null;
        this.slider = null;
        this.options = {};
        this.swiperWrapper = this.querySelector('swiper-main-product .swiper')
        this.thumbnailClickHandlers = new Map();
        this.thumbnailResizeObserver = null;
        this.handleGalleryUpdateBound = this.handleGalleryUpdate.bind(this);
        this.handleThumbnailScrollBound = this.updateThumbnailNavState.bind(this);

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.bindThumbnailClicks();

        if (this.isDesktopGalleryLayout() && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      isDesktopGalleryLayout() {
        const layout = this.dataset.desktopLayout;
        return (
          layout === 'vertical_gallery' || layout === 'horizontal_gallery'
        );
      }

      isVerticalThumbnails() {
        return this.dataset.desktopLayout === 'vertical_gallery';
      }

      isHorizontalGallery() {
        return this.dataset.desktopLayout === 'horizontal_gallery';
      }

      getGallerySlides() {
        return Array.from(
          this.swiperWrapper?.querySelectorAll('.swiper-slide') || [],
        );
      }

      getSlideIndex(slide) {
        if (!slide) return -1;
        return this.getGallerySlides().indexOf(slide);
      }

      getFirstVisibleSlide() {
        return this.getGallerySlides().find(
          (slide) => !slide.classList.contains(IS_HIDDEN),
        );
      }

      slideToFirstVisible() {
        const firstVisible = this.getFirstVisibleSlide();
        const index = this.getSlideIndex(firstVisible);
        if (index >= 0 && this.slider) {
          this.slider.slideTo(index, 0);
        }
      }

      slideToVisibleSlide(slide) {
        const index = this.getSlideIndex(slide);
        if (index >= 0) {
          this.goToSlide(index);
        }
      }

      connectedCallback() {
        if (this.swiperWrapper?.dataset.desktopSlider === 'true') {
          this.initSlider()
        } else {
          this.manageSliderInstance();
          window.addEventListener('resize', this.manageSliderInstance.bind(this));
        }

        if (this.dataset.activeColor && this.dataset.activeColor !== 'default-title') {
          this.activeColor = this.dataset.activeColor;
        }

        this.refreshElements();
        this.applyColorVisibility();

        if (this.dataset.activeColor && this.dataset.activeColor !== 'default-title') {
          this.addEventListener('colorChanged', (event) => {
            this.activeColor = event.detail.color;
            this.applyColorVisibility();
          });
        }

        this.addEventListener('galleryUpdated', this.handleGalleryUpdateBound);

        if (this.isDesktopGalleryLayout()) {
          this.initThumbnailNavigation();
        }
      }

      disconnectedCallback() {
        this.thumbnailResizeObserver?.disconnect();
      }

      manageSliderInstance() {
        const isMobile = window.innerWidth <= 768;

        if (isMobile && !this.slider) {
          this.initSlider()
        } else if (!isMobile && this.slider && this.swiperWrapper?.dataset.desktopSlider !== 'true') {
          this.slider.destroy(true, true);
          this.slider = null;
        } else if (!isMobile && !this.slider) {
          const videoSlides = this.swiperWrapper?.querySelectorAll('.product__media-video');

          videoSlides?.forEach((videoSlide) => {
            this.playActiveMedia(videoSlide);
          });
        }
      }

      initSlider() {
        const isDesktopGallery =
          this.isDesktopGalleryLayout() && window.innerWidth >= 750;
        const prevEl = this.querySelector('.product-gallery__nav.swiper-button-prev');
        const nextEl = this.querySelector('.product-gallery__nav.swiper-button-next');

        const paginationEl = this.swiperWrapper.querySelector('.swiper-pagination');

        const desktopBreakpoint = this.isHorizontalGallery()
          ? { slidesPerView: 1.4, spaceBetween: 10, loop: false }
          : { slidesPerView: 1, spaceBetween: 0, loop: false };

        this.options = {
          slidesPerView: 1.3,
          spaceBetween: 3,
          loop: !isDesktopGallery,
          breakpoints: {
            768: desktopBreakpoint,
          },
        };

        if (paginationEl) {
          this.options.pagination = {
            el: paginationEl,
            clickable: true,
          };
        }

        if (isDesktopGallery && prevEl && nextEl) {
          this.options.navigation = { prevEl, nextEl };
          this.options.breakpoints[768].navigation = { prevEl, nextEl };
        }

        this.slider = new Swiper(
          this.swiperWrapper,
          this.options,
        );

        const thisComponent = this;
        this.slider?.on('slideChange', function () {
          setTimeout(function() {
            thisComponent.onSlideChanged();
          }, 500);
        });

        if (isDesktopGallery) {
          this.slideToFirstVisible();
        }
      }

      goToSlide(idx) {
        if (!this.slider) return;
        this.slider.slideTo(idx);
      }

      refreshElements() {
        this.elements.viewer = this.querySelector('[id^="GalleryViewer"]');
        this.elements.thumbnails = this.querySelector('[id^="GalleryThumbnails"]');
        this.elements.liveRegion = this.querySelector('[id^="GalleryStatus"]');
      }

      shouldFilterByColor() {
        if (!this.activeColor || !this.dataset.hasVariantImages) return false;

        const hasColorTaggedMedia =
          this.querySelector('[data-color]') !== null;

        return hasColorTaggedMedia;
      }

      showAllMedia() {
        this.elements.viewer
          ?.querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove(IS_HIDDEN);
          });

        this.elements.thumbnails
          ?.querySelectorAll('[data-media-position]')
          .forEach((element) => {
            element.classList.remove(IS_HIDDEN);
          });
      }

      normalizeColorSlug(value) {
        if (!value) return '';
        return typeof convertTextToSlug === 'function'
          ? convertTextToSlug(value)
          : value.toLowerCase().replace(/\s+/g, '-');
      }

      colorMatches(elementColor, activeColor) {
        if (!elementColor || !activeColor) return false;

        const normalizedElement = this.normalizeColorSlug(elementColor);
        const normalizedActive = this.normalizeColorSlug(activeColor);

        return (
          elementColor === activeColor ||
          normalizedElement === normalizedActive
        );
      }

      filterImagesByColor() {
        this.applyColorVisibility();
      }

      applyColorVisibility() {
        this.refreshElements();

        if (!this.shouldFilterByColor()) {
          this.showAllMedia();
        } else {
          this.processElements.call(this, this.elements.viewer, 'li[data-media-id]');

          const visibleSlides = this.elements.viewer?.querySelectorAll(
            'li[data-media-id]:not(.is-hidden)',
          );

          if (visibleSlides?.length === 0) {
            this.elements.viewer?.querySelectorAll('li[data-media-id]').forEach((element) => {
              element.classList.remove(IS_HIDDEN);
            });
          }

          if (this.elements.thumbnails) {
            this.processElements.call(this, this.elements.thumbnails, '[data-media-position]');

            const visibleThumbs = this.elements.thumbnails.querySelectorAll(
              '.thumbnail-list__item:not(.is-hidden)',
            );

            if (visibleThumbs.length === 0) {
              this.elements.thumbnails.querySelectorAll('.thumbnail-list__item').forEach((element) => {
                element.classList.remove(IS_HIDDEN);
              });
            }

            const firstImages = this.elements.thumbnails.querySelector(
              '.thumbnail-list__item:not(.is-hidden)',
            );
            if (firstImages) {
              const activeThumbnail = this.elements.thumbnails.querySelector(
                `[data-target="${firstImages.dataset.target}"]`,
              );
              this.setActiveThumbnail(activeThumbnail);
            }
          }
        }
        this.updatePagination();

        if (this.slider) {
          this.slider.update();
          this.slideToFirstVisible();
        }

        const list = this.getThumbnailList();
        if (list) {
          list.scrollTop = 0;
          list.scrollLeft = 0;
        }

        this.syncThumbnailStrip();
      }

      processElements(container, selector) {
        if (!container) return;

        container.querySelectorAll(selector).forEach((element) => {
          const { classList, dataset } = element;

          if (!dataset.color) {
            classList.add(IS_HIDDEN);
            classList.remove(IS_ACTIVE);
            return;
          }

          if (this.colorMatches(dataset.color, this.activeColor)) {
            classList.remove(IS_HIDDEN);
          } else {
            classList.add(IS_HIDDEN);
            classList.remove(IS_ACTIVE);
          }
        });
      }

      onSlideChanged() {
        let activeSlide = this.elements.viewer?.querySelector('.swiper-slide-active');
        if (!activeSlide?.dataset?.mediaId) return;

        if (activeSlide.classList.contains(IS_HIDDEN) && this.slider) {
          const slides = this.getGallerySlides();
          const currentIndex = slides.indexOf(activeSlide);
          let nextIndex = currentIndex + 1;

          while (nextIndex < slides.length && slides[nextIndex].classList.contains(IS_HIDDEN)) {
            nextIndex += 1;
          }

          if (nextIndex < slides.length) {
            this.slider.slideTo(nextIndex);
            return;
          }

          let prevIndex = currentIndex - 1;
          while (prevIndex >= 0 && slides[prevIndex].classList.contains(IS_HIDDEN)) {
            prevIndex -= 1;
          }

          if (prevIndex >= 0) {
            this.slider.slideTo(prevIndex);
          }
          return;
        }

        const mediaId = activeSlide.dataset.mediaId;

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`,
          );
          this.setActiveThumbnail(thumbnail);
        }

        const activeMedia = this.elements.viewer.querySelector(
          `.swiper-slide-active[data-media-id="${mediaId}"]`,
        );

        if (activeMedia) this.playActiveMedia(activeMedia);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`,
        );
        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove(IS_ACTIVE);
          });
        activeMedia.classList.add(IS_ACTIVE);

        if (this.slider) {
          this.slideToVisibleSlide(activeMedia);
        }

        this.preventStickyHeader();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail?.dataset.mediaPosition,
        );
      }

      updatePagination() {
        const totalImages = this.elements.viewer.querySelectorAll('li[data-media-id]:not(.is-hidden)')
        if (totalImages.length <= 0) return;
        document.querySelectorAll('.slider-counter--total').forEach((element) => {
          element.textContent = totalImages.length;
        });
      }

      getThumbnailList() {
        return this.elements.thumbnails?.querySelector('[id^="Slider-"]');
      }

      isThumbnailInView(thumbnail, threshold = 10) {
        const list = this.getThumbnailList();
        if (!list || !thumbnail) return true;

        if (this.isVerticalThumbnails() && window.innerWidth >= 750) {
          const listRect = list.getBoundingClientRect();
          const thumbRect = thumbnail.getBoundingClientRect();
          return (
            thumbRect.top >= listRect.top - threshold &&
            thumbRect.bottom <= listRect.bottom + threshold
          );
        }

        const listRect = list.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();
        return (
          thumbRect.left >= listRect.left - threshold &&
          thumbRect.right <= listRect.right + threshold
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        const list = this.getThumbnailList();
        if (!list || this.isThumbnailInView(thumbnail, 10)) return;

        if (this.isVerticalThumbnails() && window.innerWidth >= 750) {
          list.scrollTo({
            top: thumbnail.offsetTop,
            behavior: 'smooth',
          });
        } else {
          list.scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!position) return;
        const image = activeItem.querySelector(
          '.product__modal-opener--image img',
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        this.querySelectorAll('deferred-media').forEach(() => {});
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          const video = deferredMedia.querySelector('video')
          if (video) {
            video.currentTime = 0
            video.play()
          } else {
            setTimeout(() => {
              deferredMedia.querySelector('button.deferred-media__poster')?.click();
            }, 500)
          }
          const videoHandlerButton = deferredMedia?.closest('.product-media-container')?.querySelector('.product-media__icons')
          videoHandlerButton?.setAttribute('data-video-state', 'playing')
        };
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer) return;
        this.elements.viewer.setAttribute('role', 'presentation');
      }

      handleGalleryUpdate(event) {
        const variant = event.detail.variant;

        this.refreshElements();
        this.updateGalleryDataAttributes(variant);

        const syncedActiveColor = this.getAttribute('data-active-color');
        this.activeColor = syncedActiveColor || null;

        this.applyColorVisibility();

        if (this.slider) {
          this.slider.update();

          if (this.slider.slides.length > 0) {
            this.slideToFirstVisible();
          }

          if (this.slider.pagination?.el) {
            this.slider.pagination.update();
          }
        }

        this.reinitializeSlideEvents();
        this.syncThumbnailStrip();
      }

      updateGalleryDataAttributes(variant) {
        const mediaCount = this.querySelectorAll('swiper-main-product .swiper-slide').length;
        this.dataset.totalMediaCount = mediaCount;
      }

      getActiveColorFromVariant(variant) {
        const colorOptionIndex = this.dataset.colorOptionIndex;

        if (
          colorOptionIndex !== undefined &&
          variant?.options?.length
        ) {
          const colorValue = variant.options[parseInt(colorOptionIndex, 10)];
          if (colorValue) {
            return convertTextToSlug(colorValue);
          }
        }

        return this.dataset.activeColor || null;
      }

      bindThumbnailClicks() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;

            const handler = this.setActiveMedia.bind(
              this,
              mediaToSwitch.dataset.target,
              false,
            );
            this.thumbnailClickHandlers.set(button, handler);
            button.addEventListener('click', handler);
          });
      }

      reinitializeSlideEvents() {
        const videoSlides = this.swiperWrapper?.querySelectorAll('.product__media-video');
        videoSlides?.forEach((videoSlide) => {
          this.playActiveMedia(videoSlide);
        });

        if (this.elements.thumbnails) {
          this.thumbnailClickHandlers.forEach((handler, button) => {
            button.removeEventListener('click', handler);
          });
          this.thumbnailClickHandlers.clear();
          this.bindThumbnailClicks();
        }
      }

      initThumbnailNavigation() {
        if (!this.elements.thumbnails || !this.isDesktopGalleryLayout()) return;

        const prevBtn = this.elements.thumbnails.querySelector('.thumbnail-slider__nav--prev');
        const nextBtn = this.elements.thumbnails.querySelector('.thumbnail-slider__nav--next');
        const list = this.getThumbnailList();
        const mainViewer = this.querySelector('.slider-product-main');

        if (!prevBtn || !nextBtn || !list || !mainViewer) return;

        if (!this.thumbnailNavInitialized) {
          prevBtn.addEventListener('click', () => this.scrollThumbnailStrip(-1));
          nextBtn.addEventListener('click', () => this.scrollThumbnailStrip(1));
          list.addEventListener('scroll', this.handleThumbnailScrollBound, { passive: true });
          this.thumbnailNavInitialized = true;
        }

        this.thumbnailResizeObserver?.disconnect();
        this.thumbnailResizeObserver = new ResizeObserver(() => {
          this.syncThumbnailStrip();
        });
        this.thumbnailResizeObserver.observe(mainViewer);

        this.syncThumbnailStrip();
      }

      getThumbnailScrollStep() {
        const list = this.getThumbnailList();
        if (!list) return 66;

        const visibleItem = list.querySelector('.thumbnail-list__item:not(.is-hidden)');
        if (!visibleItem) return 66;

        const isVertical = this.isVerticalThumbnails() && window.innerWidth >= 750;
        const itemSize = isVertical ? visibleItem.offsetHeight : visibleItem.offsetWidth;
        const gap = parseFloat(getComputedStyle(list).gap) || 8;

        return itemSize + gap;
      }

      scrollThumbnailStrip(direction) {
        const list = this.getThumbnailList();
        if (!list) return;

        const step = this.getThumbnailScrollStep() * direction;
        const isVertical = this.isVerticalThumbnails() && window.innerWidth >= 750;

        if (isVertical) {
          list.scrollBy({ top: step, behavior: 'smooth' });
        } else {
          list.scrollBy({ left: step, behavior: 'smooth' });
        }
      }

      updateThumbnailNavState() {
        if (!this.isDesktopGalleryLayout() || window.innerWidth < 750) return;

        const prevBtn = this.elements.thumbnails?.querySelector('.thumbnail-slider__nav--prev');
        const nextBtn = this.elements.thumbnails?.querySelector('.thumbnail-slider__nav--next');
        const list = this.getThumbnailList();

        if (!prevBtn || !nextBtn || !list) return;

        const isVertical = this.isVerticalThumbnails();
        const scrollPos = isVertical ? list.scrollTop : list.scrollLeft;
        const maxScroll = isVertical
          ? list.scrollHeight - list.clientHeight
          : list.scrollWidth - list.clientWidth;
        const hasOverflow = maxScroll > 1;

        prevBtn.toggleAttribute('disabled', !hasOverflow || scrollPos <= 1);
        nextBtn.toggleAttribute('disabled', !hasOverflow || scrollPos >= maxScroll - 1);
        prevBtn.setAttribute('aria-hidden', hasOverflow ? 'false' : 'true');
        nextBtn.setAttribute('aria-hidden', hasOverflow ? 'false' : 'true');
      }

      syncThumbnailStrip() {
        if (!this.isDesktopGalleryLayout() || window.innerWidth < 750) return;

        const thumbSlider = this.elements.thumbnails;
        const list = this.getThumbnailList();
        const mainViewer = this.querySelector('.slider-product-main');

        if (!thumbSlider || !list || !mainViewer) return;

        if (this.isVerticalThumbnails()) {
          if (mainViewer.offsetHeight > 0) {
            thumbSlider.style.maxHeight = `${mainViewer.offsetHeight}px`;
          }
        } else if (mainViewer.offsetWidth > 0) {
          thumbSlider.style.maxWidth = `${mainViewer.offsetWidth}px`;
        }

        this.updateThumbnailNavState();
      }
    },
  );
}
